import { useEffect, useState } from "react";
import {
  getReportById,
  getProducerById,
  fetchProductCategories,
  fetchReportsCsv,
  saveReport,
  deleteReport,
  publishReport,
  fetchAllReports,
  fetchReportsByProducer,
  confirmReport,
} from "@/ReactInit/Scripts/DashboardPage/api/dashboardApi";
import {
  type ProductCategory,
  type ReportDetails,
  type ProducerDetails,
  ReportSummary,
  ReportMode,
} from "@/ReactInit/Scripts/DashboardPage/types";
import {
  formatDate,
  formatTime,
  formatOrgnr,
  formatFilenameTimestamp,
  formatVolumeKg,
  formatNameWithUnderscore,
  parseDashboardDate,
  norwegianNationalPhone,
  formatNorwegianPhone,
} from "@/ReactInit/Scripts/DashboardPage/utils/dashboardFormatting";
import { DashboardCard } from "@/ReactInit/Scripts/DashboardPage/components/DashboardCard";
import {
  DashboardButton,
  DashboardIconButton,
} from "@/ReactInit/Scripts/DashboardPage/components/DashboardButton";
import ProductSelector from "@/ReactInit/Scripts/DashboardPage/Actor/components/ProductSelector";
import { DashboardAlertBox } from "@/ReactInit/Scripts/DashboardPage/components/DashboardAlertBox";
import { DashboardConfirmationModal } from "@/ReactInit/Scripts/DashboardPage/components/DashboardConfirmationModal";
import DashboardReportSummaryView from "@/ReactInit/Scripts/DashboardPage/Admin/DashboardReportSummaryView";
import { csvBlobToXlsxBlob } from "@/ReactInit/Scripts/DashboardPage/utils/dashboardExcel";
import {
  buildCategoryByProductMap,
  cn,
  getCategoryForProductName,
  getDisabledCategoriesForIndex,
  normalizeProductKey,
} from "@/ReactInit/Scripts/DashboardPage/utils/dashboardHelpers";
import { useIsMobile } from "@/ReactInit/Scripts/DashboardPage/hooks/useBreakpoint";
import DashboardReportYearSelector from "@/ReactInit/Scripts/DashboardPage/components/DashboardReportYearSelector";
import { withMinDuration } from "@/ReactInit/Scripts/DashboardPage/utils/minDuration";
import { DashboardLoader } from "@/ReactInit/Scripts/DashboardPage/components/DashboardLoader";

interface AdminDashboardReportEditPageProps {
  reportId: string;
  onBack: () => void;
  allReports: ReportSummary[];
  onReportDeleted: (reportId: string) => void;
  onReportPublished: (reportId: string) => void;
  onReportChanged?: (reportId: string) => void;
  isAdmin?: boolean;
}

type EditableCategoryBlock = {
  id: string;
  category: string | null;
  products: string[];
  volumes: string[];
  lastYearVolumes: (number | null)[];
  numberIds: (string | undefined)[];
};

type DraftStep = "view" | "summary";

const ensureArrayLength = <T,>(arr: T[], length: number, fill: T): T[] => {
  if (arr.length >= length) return arr;
  const diff = length - arr.length;
  return [...arr, ...Array(diff).fill(fill)];
};

const PUBLISH_CONFIRM_STORAGE_KEY = "dashboard.confirm.publishreport";

const DashboardReportEditView = ({
  reportId,
  onBack,
  allReports,
  onReportDeleted,
  onReportPublished,
  onReportChanged,
  isAdmin,
}: AdminDashboardReportEditPageProps) => {
  const [report, setReport] = useState<ReportDetails | null>(null);
  const [producer, setProducer] = useState<ProducerDetails | null>(null);
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleteSuccess, setIsDeleteSuccess] = useState(false);
  const [isPublishModalOpen, setIsPublishModalOpen] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [editingCategories, setEditingCategories] = useState<
    EditableCategoryBlock[]
  >([]);
  const [rowErrorsByBlock, setRowErrorsByBlock] = useState<
    Record<string, boolean[]>
  >({});
  const [isEditingProducts, setIsEditingProducts] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [producerError, setProducerError] = useState<string | null>(null);
  const [categoriesError, setCategoriesError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const hasRowErrors = Object.values(rowErrorsByBlock).some((arr) =>
    arr.some(Boolean)
  );
  const [editableYear, setEditableYear] = useState<number | null>(null);
  const [lastYearVolumesByProduct, setLastYearVolumesByProduct] = useState<
    Record<string, number>
  >({});
  const [isPublishEmptyModalOpen, setIsPublishEmptyModalOpen] = useState(false);
  const [downloadingReportId, setDownloadingReportId] = useState<string | null>(
    null
  );
  const [isDownloadingExcel, setIsDownloadingExcel] = useState(false);
  const [historyReports, setHistoryReports] =
    useState<ReportSummary[]>(allReports);
  const [yearsBlockedForEdit, setYearsBlockedForEdit] = useState<number[]>([]);
  const [isLoadingBlockedYears, setIsLoadingBlockedYears] = useState(false);

  const [draftStep, setDraftStep] = useState<DraftStep>("view");
  const [draftSummaryBlocks, setDraftSummaryBlocks] = useState<
    EditableCategoryBlock[]
  >([]);
  const [draftComment, setDraftComment] = useState<string>("");

  const [editStep, setEditStep] = useState<ReportMode>("edit");
  const [editComment, setEditComment] = useState<string>("");

  const [isConfirmingDraft, setIsConfirmingDraft] = useState(false);

  const isMobile = useIsMobile();

  useEffect(() => {
    if (report) setEditableYear(report.year);
  }, [report]);

  useEffect(() => {
    setDraftComment(report?.comment ?? "");
  }, [report?.reportId]);

  useEffect(() => {
    const load = async () => {
      try {
        setIsLoading(true);
        setError(null);
        setProducerError(null);
        setCategoriesError(null);
        setActionError(null);

        const data = await getReportById(reportId);
        setReport(data);

        try {
          const producerData = await getProducerById(data.producerId);
          setProducer(producerData);
        } catch (e) {
          console.error(e);
          setProducerError("Kunne ikke hente produsentdetaljer.");
        }

        try {
          const productCategories = await fetchProductCategories();
          setCategories(productCategories);
        } catch (e) {
          console.error(e);
          setCategoriesError("Kunne ikke hente produktkategorier.");
        }
      } catch (e) {
        console.error(e);
        setError(
          e instanceof Error ? e.message : "Ukjent feil ved henting av rapport."
        );
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [reportId]);

  useEffect(() => {
    let cancelled = false;

    const loadLastYear = async () => {
      if (!report) return;

      const basisYear = editableYear ?? report.year;
      const lastYearSummary = historyReports.find(
        (r) => r.producerId === report.producerId && r.year === basisYear - 1
      );

      if (!lastYearSummary) {
        if (!cancelled) setLastYearVolumesByProduct({});
        return;
      }

      try {
        const lastYearDetails = await getReportById(lastYearSummary.id);

        const map: Record<string, number> = {};
        (lastYearDetails.numbers ?? []).forEach((n) => {
          const k = normalizeProductKey(n.productName ?? "");
          if (!k) return;
          map[k] = (map[k] ?? 0) + n.volume;
        });

        if (!cancelled) setLastYearVolumesByProduct(map);
      } catch (e) {
        console.error(e);
        if (!cancelled) setLastYearVolumesByProduct({});
      }
    };

    void loadLastYear();

    return () => {
      cancelled = true;
    };
  }, [report, historyReports, editableYear]);

  useEffect(() => {
    setHistoryReports(allReports);
  }, [allReports]);

  useEffect(() => {
    let cancelled = false;

    const ensureHistoryReports = async () => {
      const reportDt = report ? parseDashboardDate(report.reportDate) : null;
      const isDraft = report && !reportDt;
      if (!isDraft) return;
      const hasAnyConfirmed = allReports.some(
        (r) => !!parseDashboardDate(r.reportDate)
      );
      if (hasAnyConfirmed) return;

      try {
        const confirmed = await fetchAllReports();
        if (!cancelled) setHistoryReports(confirmed);
      } catch (e) {
        console.error(e);
        if (!cancelled) setHistoryReports(allReports);
      }
    };

    void ensureHistoryReports();

    return () => {
      cancelled = true;
    };
  }, [report, allReports]);

  useEffect(() => {
    let cancelled = false;

    const loadYearsBlockedForEdit = async () => {
      const producerId = report?.producerId;
      const currentReportId = report?.reportId;

      if (!producerId || !currentReportId) {
        setYearsBlockedForEdit([]);
        return;
      }

      const fallbackYears = Array.from(
        new Set(
          allReports
            .filter(
              (r) => r.producerId === producerId && r.id !== currentReportId
            )
            .map((r) => r.year)
        )
      ).sort((a, b) => a - b);

      try {
        setIsLoadingBlockedYears(true);
        const reports = await fetchReportsByProducer(producerId);

        if (cancelled) return;

        const blockedYears = Array.from(
          new Set(
            reports
              .filter(
                (r) => r.id !== currentReportId
              )
              .map((r) => r.year)
          )
        ).sort((a, b) => a - b);

        setYearsBlockedForEdit(blockedYears);
      } catch (e) {
        console.error(e);
        if (!cancelled) setYearsBlockedForEdit(fallbackYears);
      } finally {
        if (!cancelled) setIsLoadingBlockedYears(false);
      }
    };

    void loadYearsBlockedForEdit();

    return () => {
      cancelled = true;
    };
  }, [allReports, report?.producerId, report?.reportId]);

  const buildEditingCategoriesFromReport = (
    rep: ReportDetails,
    cats: ProductCategory[]
  ): EditableCategoryBlock[] => {
    if (!rep.numbers || rep.numbers.length === 0) {
      return [];
    }

    const categoryByProduct = buildCategoryByProductMap(cats);

    const byCategory = new Map<
      string,
      {
        products: string[];
        volumes: string[];
        lastYearVolumes: (number | null)[];
        numberIds: (string | undefined)[];
      }
    >();

    rep.numbers.forEach((n) => {
      const category = getCategoryForProductName(
        n.productName,
        categoryByProduct
      );

      const entry = byCategory.get(category) ?? {
        products: [],
        volumes: [],
        lastYearVolumes: [],
        numberIds: [],
      };

      entry.products.push(n.productName);
      entry.volumes.push(String(n.volume));
      entry.lastYearVolumes.push(null);
      entry.numberIds.push(n.numberId);

      byCategory.set(category, entry);
    });

    return Array.from(byCategory.entries()).map(([category, data], index) => ({
      id: `cat-${index}`,
      category,
      products: data.products,
      volumes: data.volumes,
      lastYearVolumes: data.lastYearVolumes,
      numberIds: data.numberIds,
    }));
  };

  useEffect(() => {
    setEditComment(report?.comment ?? "");
  }, [report?.reportId]);

  useEffect(() => {
    if (!isEditingProducts) return;

    setEditingCategories((prev) =>
      hydrateBlocksWithLastYear(prev, lastYearVolumesByProduct)
    );
    setDraftSummaryBlocks((prev) =>
      hydrateBlocksWithLastYear(prev, lastYearVolumesByProduct)
    );
  }, [lastYearVolumesByProduct, isEditingProducts]);

  const handleStartEditProducts = () => {
    if (!report) return;
    if (isLocked) return;

    const blocks = buildEditingCategoriesFromReport(report, categories);
    setEditingCategories(
      hydrateBlocksWithLastYear(blocks, lastYearVolumesByProduct)
    );
    setRowErrorsByBlock({});
    setIsEditingProducts(true);
    setEditStep("edit");
    setEditComment(report.comment ?? "");
    setActionError(null);
  };

  const handleCancelEdit = () => {
    setIsEditingProducts(false);
    setEditingCategories([]);
    setRowErrorsByBlock({});
    setActionError(null);
  };

  const handleDownloadCsv = async () => {
    if (!report) return;
    try {
      setActionError(null);
      setIsDownloadingExcel(true);
      const csvBlob = await withMinDuration(() =>
        fetchReportsCsv([report.reportId])
      );
      const xlsxBlob = await csvBlobToXlsxBlob(csvBlob, {
        sheetName: "Rapport",
      });
      const url = window.URL.createObjectURL(xlsxBlob);
      const timestamp = formatFilenameTimestamp();
      const filename = `rapport-${timestamp}.xlsx`;
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      setActionError(
        e instanceof Error
          ? e.message
          : "Noe gikk galt ved nedlasting av Excel-filen."
      );
    } finally {
      setIsDownloadingExcel(false);
    }
  };

  const handleDownloadPreviousReport = async (summary: ReportSummary) => {
    if (!report) return;

    try {
      setActionError(null);
      setDownloadingReportId(summary.id);
      const csvBlob = await fetchReportsCsv([summary.id]);
      const xlsxBlob = await csvBlobToXlsxBlob(csvBlob, {
        sheetName: `${summary.year}-rapport`,
      });
      const url = window.URL.createObjectURL(xlsxBlob);
      const filename = `${formatNameWithUnderscore(report.producerName)}_${
        summary.year
      }.xlsx`;
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      setActionError(
        e instanceof Error
          ? e.message
          : "Noe gikk galt ved nedlasting av tidligere rapport."
      );
    } finally {
      setDownloadingReportId(null);
    }
  };

  const handleDeleteClick = () => {
    if (!report) return;
    if (isLocked) return;
    setActionError(null);
    setIsDeleteSuccess(false);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!report) return;

    try {
      setIsDeleting(true);
      setActionError(null);

      const guard = await guardNotPublished({ actionLabel: "Sletting" });
      if (!guard.ok) return;

      await withMinDuration(() => deleteReport(report.reportId), {
        minMs: 600,
      });
      setIsDeleteSuccess(true);
    } catch (e) {
      console.error(e);
      setActionError(
        e instanceof Error
          ? e.message
          : "Noe gikk galt ved sletting av rapporten."
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    if (isDeleting) return;
    setIsDeleteModalOpen(false);
    setIsDeleteSuccess(false);
  };

  const handleDeleteSuccessClose = () => {
    if (!report) return;
    setIsDeleteModalOpen(false);
    setIsDeleteSuccess(false);
    onReportDeleted(report.reportId);
    onBack();
  };

  const hasMissingCategory = editingCategories.some(
    (b) => !b.category || !String(b.category).trim()
  );

  const openEditSummary = () => {
    if (!report) return;
    if (isLocked) return;

    if (hasRowErrors) {
      setActionError("Du har feil i noen rader. Rett opp før du går videre.");
      return;
    }

    if (hasMissingCategory) {
      setActionError("Velg produktkategori før du går videre.");
      return;
    }

    setActionError(null);
    setEditingCategories((prev) =>
      hydrateBlocksWithLastYear(prev, lastYearVolumesByProduct)
    );
    setEditStep("summary");
  };

  const submitEditsFromSummary = async () => {
    if (!report) return;
    if (isLocked) return;

    try {
      setIsSaving(true);
      setActionError(null);

      const yearGuard = await guardYearNotDuplicated();
      if (!yearGuard.ok) return;

      const guard = await guardNotPublished({ actionLabel: "Lagring" });
      if (!guard.ok) return;

      const numbersPayload = editingCategories
        .flatMap((block) =>
          block.products.map((productName, index) => {
            const volumeRaw = block.volumes[index] ?? "";
            const volumeStr = String(volumeRaw)
              .replace(/\s/g, "")
              .replace(",", ".");
            const volumeNumber = volumeStr === "" ? NaN : Number(volumeStr);

            return {
              numberId: block.numberIds[index],
              reportId: report.reportId,
              productName,
              volume: volumeNumber,
            };
          })
        )
        .filter(
          (n) =>
            n.productName.trim() !== "" &&
            !Number.isNaN(n.volume) &&
            n.volume !== undefined &&
            n.volume !== null
        );

      await withMinDuration(() =>
        saveReport({
          reportId: report.reportId,
          producerId: report.producerId,
          producerName: report.producerName,
          orgnr: report.orgnr,
          year: editableYear ?? report.year,
          comment: editComment.trim() ? editComment.trim() : null,
          reportedBy: report.reportedBy ?? null,
          numbers: numbersPayload,
        })
      );

      const updated = await getReportById(report.reportId);
      setReport(updated);
      onReportChanged?.(report.reportId);

      setEditStep("edit");
      setIsEditingProducts(false);
      setEditingCategories([]);
      setRowErrorsByBlock({});
    } catch (e) {
      console.error(e);
      setActionError(
        e instanceof Error
          ? e.message
          : "Noe gikk galt ved lagring av rapporten."
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddCategoryBlock = () => {
    if (categories.length === 0) return;

    setEditingCategories((prev) => {
      const used = new Set(
        prev
          .map((b) => b.category)
          .filter(
            (c): c is string => typeof c === "string" && c.trim().length > 0
          )
      );

      const hasRemaining = categories.some((c) => !used.has(c.category));
      if (!hasRemaining) return prev;

      const newBlock: EditableCategoryBlock = {
        id: `new-cat-${Date.now()}`,
        category: null,
        products: [""],
        volumes: [""],
        lastYearVolumes: [null],
        numberIds: [undefined],
      };

      return [...prev, newBlock];
    });
  };

  const handlePublishClick = () => {
    if (!report) return;
    if (isLocked) return;
    if (isDraft) {
      setActionError(
        "Dette er et utkast og kan ikke sendes til Fabric før det er bekreftet."
      );
      return;
    }

    setActionError(null);
    if (!hasAnyNumbers) {
      setIsPublishEmptyModalOpen(true);
      return;
    }

    try {
      if (localStorage.getItem(PUBLISH_CONFIRM_STORAGE_KEY) === "1") {
        void handleConfirmPublish();
        return;
      }
    } catch {
      // ignore
    }

    setIsPublishModalOpen(true);
  };

  const handleCancelPublish = () => {
    if (isPublishing) return;
    setIsPublishModalOpen(false);
  };

  const handleConfirmPublish = async () => {
    if (!report) return;

    try {
      setIsPublishing(true);
      setActionError(null);

      const guard = await guardNotPublished({ actionLabel: "Publisering" });
      if (!guard.ok) return;

      await withMinDuration(() => publishReport(report.reportId));
      setIsPublishModalOpen(false);
      const updated = await getReportById(report.reportId);
      setReport(updated);
      setEditableYear(updated.year);
      onReportPublished(report.reportId);
    } catch (e) {
      console.error(e);
      const msg =
        e instanceof Error
          ? e.message
          : "Noe gikk galt ved publisering av rapporten.";
      setActionError(msg);
      throw new Error(msg);
    } finally {
      setIsPublishing(false);
    }
  };

  const openDraftSummary = async () => {
    if (!report) return;
    if (isLocked) return;

    if (isEditingProducts) {
      setActionError(
        "Fullfør eller avbryt redigeringen før du går til oppsummering."
      );
      return;
    }

    try {
      setActionError(null);

      const latest = await getReportById(report.reportId);
      setReport(latest);

      const blocks = buildEditingCategoriesFromReport(latest, categories);
      setDraftSummaryBlocks(
        hydrateBlocksWithLastYear(blocks, lastYearVolumesByProduct)
      );
      setDraftStep("summary");
    } catch (e) {
      console.error(e);
      setActionError(
        e instanceof Error ? e.message : "Kunne ikke åpne oppsummering."
      );
    }
  };

  const handleConfirmDraft = async () => {
    if (!report) return;
    if (isLocked) return;

    try {
      setIsConfirmingDraft(true);
      setActionError(null);

      const yearGuard = await guardYearNotDuplicated();
      if (!yearGuard.ok) return;

      const guard = await guardNotPublished({ actionLabel: "Bekreftelse" });
      if (!guard.ok) return;

      const numbersPayload = draftSummaryBlocks
        .flatMap((block) =>
          block.products.map((productName, index) => {
            const volumeRaw = block.volumes[index] ?? "";
            const volumeStr = String(volumeRaw)
              .replace(/\s/g, "")
              .replace(",", ".");
            const volumeNumber = volumeStr === "" ? NaN : Number(volumeStr);

            return {
              numberId: block.numberIds[index],
              reportId: report.reportId,
              productName,
              volume: volumeNumber,
            };
          })
        )
        .filter(
          (n) =>
            n.productName.trim() !== "" &&
            !Number.isNaN(n.volume) &&
            n.volume !== undefined &&
            n.volume !== null
        );

      await withMinDuration(() =>
        saveReport({
          reportId: report.reportId,
          producerId: report.producerId,
          producerName: report.producerName,
          orgnr: report.orgnr,
          year: editableYear ?? report.year,
          comment: draftComment.trim() ? draftComment.trim() : null,
          reportedBy: report.reportedBy ?? null,
          numbers: numbersPayload,
        })
      );

      await withMinDuration(() =>
        confirmReport({
          reportId: report.reportId,
          reporterName: "OFG Admin",
          phoneNumber: "",
        })
      );

      const updated = await getReportById(report.reportId);
      setReport(updated);

      setDraftStep("view");
    } catch (e) {
      console.error(e);
      setActionError(
        e instanceof Error
          ? e.message
          : "Noe gikk galt ved bekreftelse av rapporten."
      );
    } finally {
      setIsConfirmingDraft(false);
    }
  };

  const removeCategoryBlock = (blockId: string) => {
    setEditingCategories((prev) => prev.filter((b) => b.id !== blockId));
    setRowErrorsByBlock((prev) => {
      const copy = { ...prev };
      delete copy[blockId];
      return copy;
    });
  };

  const updateCategory = (blockIndex: number, category: string | null) => {
    setEditingCategories((prev) =>
      prev.map((block, i) => {
        if (i !== blockIndex) return block;

        if ((block.category ?? "") === (category ?? "")) return block;

        return {
          ...block,
          category,
          products: [""],
          volumes: [""],
          lastYearVolumes: [null],
          numberIds: [undefined],
        };
      })
    );
  };

  const updateProduct = (
    blockIndex: number,
    rowIndex: number,
    product: string
  ) => {
    setEditingCategories((prev) =>
      prev.map((block, i) => {
        if (i !== blockIndex) return block;

        const targetLength = rowIndex + 1;

        const products = ensureArrayLength(block.products, targetLength, "");
        const volumes = ensureArrayLength(block.volumes, targetLength, "");
        const lastYearVolumes = ensureArrayLength(
          block.lastYearVolumes,
          targetLength,
          null
        );
        const numberIds = ensureArrayLength(
          block.numberIds,
          targetLength,
          undefined
        );

        products[rowIndex] = product;

        return {
          ...block,
          products,
          volumes,
          lastYearVolumes,
          numberIds,
        };
      })
    );
  };

  const updateVolume = (
    blockIndex: number,
    rowIndex: number,
    volume: string
  ) => {
    setEditingCategories((prev) =>
      prev.map((block, i) => {
        if (i !== blockIndex) return block;

        const targetLength = rowIndex + 1;

        const products = ensureArrayLength(block.products, targetLength, "");
        const volumes = ensureArrayLength(block.volumes, targetLength, "");
        const lastYearVolumes = ensureArrayLength(
          block.lastYearVolumes,
          targetLength,
          null
        );
        const numberIds = ensureArrayLength(
          block.numberIds,
          targetLength,
          undefined
        );

        volumes[rowIndex] = volume;

        return {
          ...block,
          products,
          volumes,
          lastYearVolumes,
          numberIds,
        };
      })
    );
  };

  const updateLastYearVolume = (
    blockIndex: number,
    rowIndex: number,
    value: number | null
  ) => {
    setEditingCategories((prev) =>
      prev.map((block, i) => {
        if (i !== blockIndex) return block;

        const targetLength = rowIndex + 1;

        const products = ensureArrayLength(block.products, targetLength, "");
        const volumes = ensureArrayLength(block.volumes, targetLength, "");
        const lastYearVolumes = ensureArrayLength(
          block.lastYearVolumes,
          targetLength,
          null
        );
        const numberIds = ensureArrayLength(
          block.numberIds,
          targetLength,
          undefined
        );

        lastYearVolumes[rowIndex] = value;

        return {
          ...block,
          products,
          volumes,
          lastYearVolumes,
          numberIds,
        };
      })
    );
  };

  const hydrateBlocksWithLastYear = (
    blocks: EditableCategoryBlock[],
    lastYearVolumesByProduct: Record<string, number>
  ): EditableCategoryBlock[] => {
    return blocks.map((b) => ({
      ...b,
      lastYearVolumes: b.products.map((p, idx) => {
        const key = normalizeProductKey(p ?? "");
        const v =
          typeof lastYearVolumesByProduct[key] === "number"
            ? lastYearVolumesByProduct[key]
            : null;

        return b.lastYearVolumes?.[idx] ?? v;
      }),
    }));
  };

  if (isLoading) {
    return <DashboardLoader label="Laster rapport…" />;
  }

  if (error || !report) {
    return (
      <div className="flex flex-col gap-4">
        <DashboardButton
          label="Tilbake til rapportoversikt"
          variant="underline"
          onClick={onBack}
          icon="icon-arrow-left"
        />
        {error ? (
          <DashboardAlertBox type="error" label={error} />
        ) : (
          <DashboardAlertBox type="info" label="Fant ingen rapport." />
        )}
      </div>
    );
  }

  const producerName = producer?.producerName ?? report.producerName;
  const producerOrgnr = producer?.orgnr ?? report.orgnr;
  const reportedByRaw = (report.reportedBy ?? "").trim();

  const reportedByNational = norwegianNationalPhone(reportedByRaw);

  const reporterPhoneEntry = reportedByNational
    ? producer?.phoneNumbers?.find(
        (p) =>
          norwegianNationalPhone(p.phoneNumber ?? "") === reportedByNational
      )
    : undefined;

  const reporterName = reporterPhoneEntry?.name?.trim()
    ? reporterPhoneEntry.name
    : reportedByRaw || "–";

  const reporterPhone = reportedByNational ? reportedByNational : "–";

  const reporterEmail = reporterPhoneEntry?.email?.trim()
    ? reporterPhoneEntry.email
    : "–";

  const year = report.year;
  const lastYear = year - 1;

  const categoryByProduct = buildCategoryByProductMap(categories);

  const productRows =
    report.numbers?.map((n) => {
      const category = getCategoryForProductName(
        n.productName,
        categoryByProduct
      );

      const key = normalizeProductKey(n.productName ?? "");
      const lastYearVol =
        typeof lastYearVolumesByProduct[key] === "number"
          ? lastYearVolumesByProduct[key]
          : null;

      return {
        productName: n.productName,
        category,
        volumeThisYear: n.volume,
        volumeLastYear: lastYearVol,
      };
    }) ?? [];

  productRows.sort((a, b) => {
    if (a.category === b.category) {
      return a.productName.localeCompare(b.productName, "nb");
    }
    return a.category.localeCompare(b.category, "nb");
  });

  const previousReports = historyReports
    .filter(
      (r) => r.producerId === report.producerId && r.id !== report.reportId
    )
    .sort((a, b) => b.year - a.year);

  const hasAnyNumbers = (report.numbers?.length ?? 0) > 0;

  const handleCancelEmptyPublish = () => {
    if (isPublishing) return;
    setIsPublishEmptyModalOpen(false);
  };

  const reportDt = parseDashboardDate(report.reportDate);
  const publishDt = parseDashboardDate(report.publishDate);
  const changedDt = parseDashboardDate(report.changedDate);

  const isDraft = !reportDt;

  const isEdited =
    !!changedDt && !!reportDt && changedDt.getTime() > reportDt.getTime();

  const sentToFabric =
    !!publishDt && (!reportDt || publishDt.getTime() > reportDt.getTime());

  const isLocked = sentToFabric || report.published;

  const computeIsSentToFabric = (r: ReportDetails): boolean => {
    const reportDt = parseDashboardDate(r.reportDate);
    const publishDt = parseDashboardDate(r.publishDate);

    const sentByDates =
      !!publishDt && (!reportDt || publishDt.getTime() > reportDt.getTime());

    return sentByDates || !!r.published;
  };

  const guardNotPublished = async (opts?: { actionLabel?: string }) => {
    if (!report) return { ok: false as const };
    const latest = await getReportById(report.reportId);
    const lockedNow = computeIsSentToFabric(latest);
    setReport(latest);
    setEditableYear(latest.year);

    if (lockedNow) {
      const action = opts?.actionLabel ?? "Denne handlingen";
      setActionError(
        `${action} er ikke mulig fordi rapporten allerede er sendt til Fabric. Oppdater siden for å se status.`
      );
      return { ok: false as const, latest };
    }

    return { ok: true as const, latest };
  };

  const guardYearNotDuplicated = async () => {
    if (!report) return { ok: false as const };

    const targetYear = editableYear ?? report.year;

    try {
      const reports = await fetchReportsByProducer(report.producerId);

      const blockedYears = Array.from(
        new Set(
          reports
            .filter((r) => r.id !== report.reportId)
            .map((r) => r.year)
        )
      ).sort((a, b) => a - b);

      setYearsBlockedForEdit(blockedYears);

      const hasDuplicate = reports.some(
        (r) => r.id !== report.reportId && r.year === targetYear
      );

      if (hasDuplicate) {
        setActionError(
          `Det finnes allerede en rapport for ${targetYear}. Velg et annet år.`
        );
        return { ok: false as const };
      }

      return { ok: true as const };
    } catch (e) {
      console.error(e);
      setActionError(
        "Kunne ikke kontrollere eksisterende rapporter for virksomheten."
      );
      return { ok: false as const };
    }
  };

  if (isDraft && draftStep === "summary") {
    return (
      <DashboardReportSummaryView
        year={report.year}
        blocks={draftSummaryBlocks}
        reportComment={draftComment}
        setReportComment={setDraftComment}
        onPrev={() => setDraftStep("view")}
        onSubmit={() => void handleConfirmDraft()}
        submitLoading={isConfirmingDraft}
      />
    );
  }

  if (isEditingProducts && editStep === "summary" && report) {
    return (
      <DashboardReportSummaryView
        year={editableYear ?? report.year}
        blocks={editingCategories}
        reportComment={editComment}
        setReportComment={setEditComment}
        onPrev={() => setEditStep("edit")}
        onSubmit={() => void submitEditsFromSummary()}
        submitLoading={isSaving}
      />
    );
  }

  const canAddMoreCategories = categories.some(
    (c) => !new Set(editingCategories.map((b) => b.category)).has(c.category)
  );
  const yearsBlockedForEditSet = new Set(yearsBlockedForEdit);

  return (
    <div className="flex flex-col gap-5 w-full mx-auto max-w-[932px]">
      {producerError && (
        <DashboardAlertBox type="warning" label={producerError} />
      )}
      {categoriesError && (
        <DashboardAlertBox type="warning" label={categoriesError} />
      )}
      {actionError && <DashboardAlertBox type="error" label={actionError} />}
      {isDraft && !isLocked && (
        <DashboardAlertBox
          type="info"
          label="Dette er et utkast. Du kan fortsette redigeringen før rapporten bekreftes."
        />
      )}
      {isEdited && !isLocked && !isDraft && (
        <DashboardAlertBox type="info" label="Denne rapporten er redigert." />
      )}
      {isLocked && (
        <DashboardAlertBox
          type="success"
          label="Denne rapporten er sendt til Fabric og kan ikke redigeres."
        />
      )}

      <DashboardCard as="section">
        <div className="grid text-base font-semibold grid-cols-2 tablet:grid-cols-4 gap-x-5 gap-y-6">
          <div className="flex flex-col gap-3">
            <p className="text-black-subtile">Virksomhet</p>
            <p>{producerName}</p>
          </div>

          <div className="flex flex-col gap-3">
            <p className="text-black-subtile">
              {isMobile ? "Org.nr." : "Organisasjonsnummer"}
            </p>
            <p>{formatOrgnr(producerOrgnr)}</p>
          </div>

          <div className="flex flex-col gap-3">
            <p className="text-black-subtile">Rapportør</p>
            <p className="truncate" title={reporterName}>
              {reporterName}
            </p>
          </div>

          <div className="flex flex-col gap-3">
            <p className="text-black-subtile">Telefonnummer</p>
            <p>{formatNorwegianPhone(reporterPhone)}</p>
          </div>

          <div className="flex flex-col gap-3">
            <p className="text-black-subtile">E-post</p>
            <p className="truncate" title={reporterEmail}>
              {reporterEmail}
            </p>
          </div>

          <div className="flex flex-col gap-3">
            <p className="text-black-subtile">
              {isDraft ? "Status" : "Rapportert dag"}
            </p>
            <p>{isDraft ? "Utkast" : formatDate(reportDt)}</p>
          </div>

          <div className="flex flex-col gap-3">
            <p className="text-black-subtile">
              {isDraft ? "Bekreftet" : "Tid"}
            </p>
            <p>{isDraft ? "Nei" : formatTime(reportDt)}</p>
          </div>

          {!isDraft && (
            <div className="flex flex-col gap-3">
              <p className="text-black-subtile">Publisert til Fabric</p>
              <p>{sentToFabric ? "Ja" : "Nei"}</p>
            </div>
          )}
        </div>
      </DashboardCard>

      {!isEditingProducts ? (
        <DashboardCard
          as="section"
          className="flex flex-col gap-5 tablet:gap-10"
        >
          <div className="flex flex-col gap-5">
            <h2 className="text-3xl font-semibold">{year}-rapport</h2>

            {productRows.length === 0 ? (
              <DashboardAlertBox
                label={`Ingen produkter rapportert for ${year}`}
                type="warning"
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="dashboardTable reportTable">
                  <thead>
                    <tr>
                      <th>Produkt</th>
                      <th>Produktkategori</th>
                      <th>{`Volum i ${year}`}</th>
                      <th>{`Volum i ${lastYear}`}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {productRows.map((row) => (
                      <tr key={`${row.category}-${row.productName}`}>
                        <td
                          className="max-w-40 truncate"
                          title={row.productName}
                        >
                          {row.productName}
                        </td>
                        <td>{row.category}</td>
                        <td className="text-black-text font-semibold">
                          {formatVolumeKg(row.volumeThisYear, { empty: "–" })}
                        </td>
                        <td>
                          {formatVolumeKg(row.volumeLastYear, { empty: "–" })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {report.comment && (
            <div className="flex flex-col gap-3">
              <h2 className="text-3xl font-semibold">Kommentar</h2>
              <p>{report.comment}</p>
            </div>
          )}

          <div className="flex flex-col tablet:flex-row justify-center gap-3">
            {!isLocked && (
              <DashboardButton
                label={isDraft ? "Fortsett redigering" : "Rediger rapport"}
                variant="underline"
                icon="icon-square-pen"
                onClick={handleStartEditProducts}
              />
            )}
            <DashboardButton
              label="Last ned Excel-fil"
              variant="underline"
              icon="icon-download"
              onClick={handleDownloadCsv}
              loading={isDownloadingExcel}
              loadingText="Laster ned…"
              disabled={
                isDraft ||
                isDownloadingExcel ||
                downloadingReportId !== null ||
                isPublishing ||
                isEditingProducts
              }
            />
            {!isLocked && (
              <DashboardButton
                label={isDraft ? "Slett foreløpig rapport" : "Slett rapport"}
                variant="underline"
                icon="icon-trash"
                onClick={handleDeleteClick}
              />
            )}
          </div>
        </DashboardCard>
      ) : (
        <div className="flex flex-col gap-4">
          {editingCategories.map((block, i) => {
            const selectedCategories = editingCategories.map((b) => b.category);
            const disabledCategories = getDisabledCategoriesForIndex(
              selectedCategories,
              i
            );

            return (
              <DashboardCard as="section" key={block.id} className="relative">
                <ProductSelector
                  selectedCategory={block.category}
                  disabledCategories={disabledCategories}
                  products={block.products}
                  volumes={block.volumes}
                  reportYear={editableYear ?? report.year}
                  lastYearVolumes={block.lastYearVolumes}
                  lastYearVolumesByProduct={lastYearVolumesByProduct}
                  onCategoryChange={(category) => updateCategory(i, category)}
                  onProductChange={(rowIndex, product) =>
                    updateProduct(i, rowIndex, product)
                  }
                  onVolumeChange={(rowIndex, volume) =>
                    updateVolume(i, rowIndex, volume)
                  }
                  onLastYearVolumeChange={(rowIndex, value) =>
                    updateLastYearVolume(i, rowIndex, value)
                  }
                  onRowErrorStateChange={(rowIndex, hasError) => {
                    setRowErrorsByBlock((prev) => {
                      const updated = { ...prev };
                      const arr = updated[block.id]
                        ? [...updated[block.id]]
                        : [];
                      arr[rowIndex] = hasError;
                      updated[block.id] = arr;
                      return updated;
                    });
                  }}
                  headerRightSlot={
                    isAdmin && !isLocked ? (
                      <DashboardReportYearSelector
                        value={editableYear ?? report.year}
                        onChange={(nextYear) => {
                          if (yearsBlockedForEditSet.has(nextYear)) return;
                          setEditableYear(nextYear);
                        }}
                        disabled={isSaving || isPublishing || isLoadingBlockedYears}
                        disabledYears={yearsBlockedForEdit}
                        disabledYearReason="Det finnes allerede en rapport for dette året."
                      />
                    ) : null
                  }
                />

                {editingCategories.length > 1 && (
                  <DashboardIconButton
                    label="Fjern produktkategori"
                    variant="sidebar"
                    rounded="full"
                    size="3"
                    icon="icon-x"
                    onClick={() => removeCategoryBlock(block.id)}
                    className="absolute top-2 right-2"
                  />
                )}
              </DashboardCard>
            );
          })}

          <DashboardButton
            label="Legg til produktkategori"
            variant="sidebar"
            onClick={handleAddCategoryBlock}
            disabled={!canAddMoreCategories}
            icon="icon-circle-plus"
            className={cn(isMobile ? "!py-3" : "!py-6", "!rounded-[100px]")}
          />
        </div>
      )}

      {isEditingProducts && (
        <form
          onSubmit={(e) => e.preventDefault()}
          className="flex flex-col gap-6"
        >
          <div className="flex flex-row justify-center gap-3">
            <DashboardButton
              label="Avbryt"
              variant="underline"
              onClick={handleCancelEdit}
              icon="icon-x"
              disabled={isSaving}
              scrollToId="dashboard-inner"
              scrollOptions={{
                durationMs: 400,
                easing: "easeInOutCubic",
                offsetTop: 15,
              }}
            />

            <DashboardButton
              label="Neste"
              variant="darkplain"
              onClick={openEditSummary}
              icon="icon-arrow-right"
              loading={isSaving}
              loadingText="Laster…"
              disabled={isSaving || hasRowErrors || hasMissingCategory}
              scrollToId="dashboard-inner"
              scrollOptions={{
                durationMs: 400,
                easing: "easeInOutCubic",
                offsetTop: 15,
              }}
            />
          </div>
        </form>
      )}

      {!isEditingProducts && (
        <div className="flex flex-row justify-center gap-3 mt-4">
          <DashboardButton
            label="Gå tilbake"
            variant="underline"
            onClick={onBack}
            icon="icon-arrow-left"
            scrollToId="dashboard"
            scrollOptions={{ durationMs: 400, easing: "easeInOutCubic" }}
          />
          {!isLocked &&
            (isDraft ? (
              <>
                {/*
                <DashboardButton
                  label="Fortsett redigering"
                  variant="darkplain"
                  onClick={handleContinueDraft}
                  icon="icon-square-pen"
                  disabled={isSaving || isPublishing}
                  scrollToId="dashboard-inner"
                  scrollOptions={{durationMs: 400, easing: "easeInOutCubic", offsetTop: 15}}
                />
                */}
                <DashboardButton
                  label="Neste"
                  variant="darkplain"
                  icon="icon-arrow-right"
                  onClick={() => void openDraftSummary()}
                  disabled={
                    isSaving ||
                    isPublishing ||
                    isEditingProducts ||
                    isConfirmingDraft
                  }
                />
              </>
            ) : (
              <DashboardButton
                label={isPublishing ? "Sender…" : "Send til Fabric"}
                variant="darkplain"
                onClick={handlePublishClick}
                icon="icon-check"
                disabled={isPublishing || report.published || isEditingProducts}
              />
            ))}
        </div>
      )}
      {previousReports.length > 0 && (
        <div className="flex flex-col gap-4 mt-10">
          <h2 className="text-4xl font-semibold">Tidligere rapporter</h2>

          <div className="grid grid-cols-1 tablet:grid-cols-2 gap-4">
            {previousReports.map((r) => {
              const filename = `${formatNameWithUnderscore(
                report.producerName
              )}_${r.year}.xlsx`;
              return (
                <DashboardCard
                  className="flex flex-col gap-3"
                  size="slim"
                  key={r.id}
                >
                  <div className="text-lg leading-6 font-semibold truncate">
                    {filename}
                  </div>
                  <DashboardButton
                    label="Last ned"
                    variant="underline"
                    icon="icon-download"
                    iconPosition="right"
                    disabled={
                      downloadingReportId !== null &&
                      downloadingReportId !== r.id
                    }
                    onClick={() => handleDownloadPreviousReport(r)}
                    loading={downloadingReportId === r.id}
                    loadingText="Laster ned…"
                  />
                </DashboardCard>
              );
            })}
          </div>
        </div>
      )}
      <DashboardConfirmationModal
        isOpen={isDeleteModalOpen}
        type="alert"
        title="Er du sikker på at du vil slette rapporten?"
        description="Ved å slette rapporten mister du all data og informasjon i den, og handlingen kan ikke angres."
        confirmLabel={isDeleting ? "Sletter…" : "Ja, slett rapporten"}
        cancelLabel="Gå tilbake"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        confirmDisabled={isDeleting}
        cancelDisabled={isDeleting}
        isSuccess={isDeleteSuccess}
        successTitle="Rapporten er slettet"
        successButtonLabel="Ok"
        onSuccessClose={handleDeleteSuccessClose}
      />
      <DashboardConfirmationModal
        isOpen={isPublishModalOpen}
        type="confirmation"
        title="Er du sikker på at du vil sende til Fabric?"
        description="Dette betyr at dataene i rapporten blir lagret og inkludert i Power BI-diagrammet. De publiserte dataene vil være tilgjengelige for offentligheten."
        confirmLabel={isPublishing ? "Sender…" : "Ja, send til Fabric"}
        cancelLabel="Gå tilbake til rapporten"
        onConfirm={handleConfirmPublish}
        onCancel={handleCancelPublish}
        confirmDisabled={isPublishing}
        cancelDisabled={isPublishing}
        rememberKey={PUBLISH_CONFIRM_STORAGE_KEY}
        rememberLabel="Ikke vis denne meldingen neste gang"
      />
      <DashboardConfirmationModal
        isOpen={isPublishEmptyModalOpen}
        type="confirmation"
        title="Er du sikker på at du vil sende en tom rapport til Fabric?"
        description="Denne rapporten inneholder ingen produkter/volum. Dette er sjeldent riktig. Er du helt sikker på at du vil sende en tom rapport til Fabric?"
        confirmLabel={
          isPublishing ? "Sender…" : "Ja, send tom rapport til Fabric"
        }
        cancelLabel="Gå tilbake"
        onConfirm={async () => {
          await handleConfirmPublish();
          setIsPublishEmptyModalOpen(false);
        }}
        onCancel={handleCancelEmptyPublish}
        confirmDisabled={isPublishing}
        cancelDisabled={isPublishing}
      />
    </div>
  );
};

export default DashboardReportEditView;
